import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";

const links = [
  { to: "/", label: "Index", end: true },
  { to: "/services", label: "Services" },
  { to: "/sectors", label: "Sectors" },
  { to: "/cities", label: "Coverage" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "Standard" },
  { to: "/contact", label: "Contact" },
];

export const CONTACT_PHONE = "(807) 907-2874";
export const CONTACT_PHONE_HREF = "tel:+18079072874";
export const CONTACT_EMAIL = "info@mazesecurity.ca";
export const CONTACT_EMAIL_HREF = "mailto:info@mazesecurity.ca";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,border-color] duration-500 ${
          scrolled
            ? "bg-[#050505]/90 backdrop-blur-xl border-b border-[#1e1e1e]"
            : "bg-[#050505]/60 backdrop-blur-md border-b border-transparent"
        }`}
      >
        {/* Primary row: brand + nav */}
        <div className="mx-auto max-w-[1600px] px-4 md:px-10 flex items-center justify-between py-3 md:py-4">
          <Link
            to="/"
            data-testid="nav-brand"
            className="group flex items-center gap-2 md:gap-3 min-w-0"
          >
            <span
              aria-hidden
              className="inline-flex h-8 w-8 md:h-9 md:w-9 flex-shrink-0 items-center justify-center border border-[#FF3B30] text-[#FF3B30] font-display font-black text-lg leading-none group-hover:bg-[#FF3B30] group-hover:text-[#050505] transition-colors duration-500"
            >
              M
            </span>
            <span
              className="font-display leading-tight min-w-0"
              data-testid="nav-brand-wordmark"
            >
              <span className="block text-[10px] sm:text-[11px] md:text-[13px] uppercase tracking-[0.16em] md:tracking-[0.2em] text-white font-black">
                Maze Security Solutions
              </span>
              <span className="block text-[9px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.16em] md:tracking-[0.22em] text-[#a0a0a0]">
                Manitoba Security
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline font-body text-[13px] uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "text-white" : "text-[#a0a0a0] hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden text-white p-2 flex-shrink-0"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Contact bar — visible on ALL screens, including mobile */}
        <div
          data-testid="contact-bar"
          className="border-t border-[#1e1e1e] bg-[#050505]/95"
        >
          <div className="mx-auto max-w-[1600px] px-4 md:px-10 py-2 flex items-center justify-center md:justify-end gap-4 md:gap-8">
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="contact-bar-phone"
              className="group inline-flex items-center gap-2 font-body uppercase tracking-[0.12em] md:tracking-[0.14em] text-[10px] sm:text-[11px] md:text-xs text-white/85 hover:text-[#FF3B30] transition-colors"
            >
              <Phone size={13} className="text-[#FF3B30]" strokeWidth={2.2} />
              <span>{CONTACT_PHONE}</span>
            </a>
            <span aria-hidden className="h-3 w-px bg-[#2a2a2a]" />
            <a
              href={CONTACT_EMAIL_HREF}
              data-testid="contact-bar-email"
              className="group inline-flex items-center gap-2 font-body uppercase tracking-[0.12em] md:tracking-[0.14em] text-[10px] sm:text-[11px] md:text-xs text-white/85 hover:text-[#FF3B30] transition-colors"
            >
              <Mail size={13} className="text-[#FF3B30]" strokeWidth={2.2} />
              <span className="truncate">{CONTACT_EMAIL}</span>
            </a>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 bg-[#050505] pt-32 px-6 overflow-y-auto"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-8 pb-16">
              {links.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className="font-display text-4xl uppercase tracking-tight text-white"
                >
                  <span className="font-editorial italic text-[#FF3B30] text-lg align-top mr-3">
                    0{i + 1}
                  </span>
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-8 pt-8 border-t border-[#1e1e1e] space-y-4">
                <a
                  href={CONTACT_PHONE_HREF}
                  className="flex items-center gap-3 text-white font-display text-xl"
                >
                  <Phone size={18} className="text-[#FF3B30]" />
                  {CONTACT_PHONE}
                </a>
                <a
                  href={CONTACT_EMAIL_HREF}
                  className="flex items-center gap-3 text-white font-display text-lg break-all"
                >
                  <Mail size={18} className="text-[#FF3B30]" />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
